import { useContext, useEffect, useState } from 'react'
import CustomPaper from '../../../containers/Paper'
import { StatisticsContext } from '../../../context/StatisticsContext'
import { Chip, CircularProgress } from '@mui/material'
import CustomButton from '../../../components/Button'
import CustomModal from '../../../containers/Modal'
import SearchTagsStatisticsModal from './SearchTagsStatisticsModal'
import StatisticsService from '../../../services/StatisticsService'
import { Bar } from 'react-chartjs-2'
import { statisticsColors } from '../StatisticsPage'
import NoResults from '../../../containers/NoResults'

interface DataType {
    jobTitle: string
    amount: number
    percentage: number
}

const CompareJobTitles = () => {
    const { startDate, endDate, carreer } = useContext(StatisticsContext)
    const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)
    const [tags, setTags] = useState<string[]>([])
    const [data, setData] = useState<DataType[]>()

    useEffect(() => {
        if (tags.length > 0) {
            new StatisticsService()
                .searchGraduatesInJobTitles(tags, carreer, startDate, endDate)
                .then((response) => {
                    if (response.status !== 200) return
                    setData(response.data)
                })
        }
    }, [carreer, startDate, endDate, tags])

    const getChartData = () => {
        return {
            labels: data?.map((x) => x.jobTitle),
            datasets: [
                {
                    label: 'Cantidad de graduados en el puesto laboral',
                    data: data?.map((x) => x.amount ?? 0),
                    backgroundColor: statisticsColors.background,
                    borderColor: statisticsColors.border,
                    borderWidth: 1,
                },
            ],
        }
    }

    const options = {
        scales: {
            y: {
                beginAtZero: true,
                ticks: { stepSize: 5 },
            },
        },
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label(context: any) {
                        const label: string = context.dataset.label
                        const value = context.raw
                        const percentage =
                            Number(
                                data
                                    ?.find((x) => x.jobTitle === context.label)
                                    ?.percentage.toFixed(2),
                            ) ?? 0
                        return `${label}: ${value} (${percentage}% del total)`
                    },
                },
            },
        },
    }

    return (
        <CustomPaper className={`flex flex-col gap-2 text-primary`}>
            <span className="text-lg font-semibold">Comparador de puestos laborales</span>
            <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2 h-fit">
                    <div className="w-[120px]">
                        <CustomButton
                            className="w-fit"
                            onClick={() => setIsSearchOpen(true)}
                        >
                            Seleccionar
                        </CustomButton>
                    </div>
                    {tags.length > 0 ? (
                        <div className="flex gap-2 items-center my-2 flex-wrap">
                            {tags.map((tag) => (
                                <div key={`selected${tag}`}>
                                    <Chip
                                        label={tag}
                                        className="bg-optional text-secondary font-[500]"
                                        onDelete={() =>
                                            setTags((prev) => prev.filter((x) => x !== tag))
                                        }
                                        sx={{
                                            '& .MuiChip-deleteIcon': {
                                                color: '#434a73',
                                            },
                                            '&:hover .MuiChip-deleteIcon': {
                                                color: '#434a73',
                                            },
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span className="text-xs md:text-base">
                            Seleccioná los puestos laborales que quieras buscar y comparar.
                        </span>
                    )}
                </div>
                {tags.length > 0 ? (
                    data !== undefined ? (
                        data.length > 0 ? (
                            <div className="flex justify-center items-center h-[250px]">
                                <Bar
                                    data={getChartData()}
                                    options={options}
                                />
                            </div>
                        ) : (
                            <div className="w-full flex">
                                <NoResults
                                    title="No hay puestos laborales para mostrar."
                                    subtitle="Intentá modificando los filtros o los puestos laborales elegidos."
                                />
                            </div>
                        )
                    ) : (
                        <div className="w-full h-full flex justify-center items-center">
                            <CircularProgress
                                className="m-2 md:m-5 text-primary"
                                size={64}
                            />
                        </div>
                    )
                ) : (
                    <div className="w-full flex">
                        <NoResults
                            title="No hay puestos laborales para mostrar."
                            subtitle="Buscá y seleccioná los puestos laborales que quieras comparar."
                        />
                    </div>
                )}
            </div>
            <CustomModal
                isOpen={isSearchOpen}
                handleClose={() => setIsSearchOpen(false)}
            >
                <SearchTagsStatisticsModal
                    text="Seleccionar puestos laborales"
                    handleClose={() => setIsSearchOpen(false)}
                    type="jobTitles"
                    selectedTags={tags ?? []}
                    setSelectedTag={(tag: string) =>
                        setTags((prev) =>
                            prev.includes(tag) ? prev.filter((x) => x !== tag) : [...prev, tag],
                        )
                    }
                />
            </CustomModal>
        </CustomPaper>
    )
}

export default CompareJobTitles
